/** AI Metrics tab — admin dashboard for AI/LLM usage analytics.
 *
 * Renders KPI cards (total tokens, cost, calls), a daily token trend chart,
 * breakdown by feature and model, and a recent AI calls table.
 * A shared time-range filter (7d/30d/90d) drives every metric.
 */

import {
    AlertCircle,
    Brain,
    Cpu,
    DollarSign,
    Hash,
    Info,
    RefreshCw,
    Zap,
    type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, type ReactNode } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { DateRangePicker } from "@/shared/ui/date-range-picker";
import { T } from "@/shared/ui/DesignLib";
import { PaginationControls } from "@/shared/ui/pagination-controls";
import { Skeleton } from "@/shared/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/shared/ui/table";
import {
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Tooltip as UITooltip,
} from "@/shared/ui/tooltip";

import {
    getTimeFormat,
    getTimezone,
    getTimezoneLabel,
} from "@/shared/lib/timezone";
import { useAIMetrics } from "../hooks/useAIMetrics";
import { useAIRecentCalls } from "../hooks/useAIRecentCalls";
import {
    dateRangeToMetricsRange,
    isCustomDateRange,
    metricsRangeToDateRange,
    type MetricsRange,
    type TimeRange,
} from "../lib/metricsTypes";
import MetricCard from "./MetricCard";

const RANGE_LABELS: Record<TimeRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

function rangeLabel(range: MetricsRange): string {
  if (isCustomDateRange(range)) return `${range.from} – ${range.to}`;
  return RANGE_LABELS[range];
}

const CHART_COLORS = [T.primary, T.purple, T.amber, T.green, "#38bdf8", "#fb7185"];

const AXIS_STYLE = {
  fontSize: 10,
  fontFamily: "var(--font-mono)",
  fill: "var(--muted-foreground)",
} as const;

const TOOLTIP_STYLE = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  color: "var(--foreground)",
} as const;

const TOOLTIP_ITEM_STYLE = {
  color: "var(--foreground)",
} as const;

const TOOLTIP_LABEL_STYLE = {
  color: "var(--muted-foreground)",
} as const;

function wrapText(text: string, maxChars: number): string[] {
  if (text.length <= maxChars) return [text];
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current.length + (current ? 1 : 0) + word.length <= maxChars) {
      current = current ? `${current} ${word}` : word;
    } else {
      if (current) lines.push(current);
      if (word.length > maxChars) {
        let w = word;
        while (w.length > maxChars) {
          lines.push(w.slice(0, maxChars));
          w = w.slice(maxChars);
        }
        current = w;
      } else {
        current = word;
      }
    }
  }
  if (current) lines.push(current);
  return lines;
}

function WrappedYTick({ x, y, payload }: { x: number | string; y: number | string; payload: { value: string | number } }) {
  const text = String(payload.value);
  const lines = wrapText(text, 16);
  const lineHeight = 1.2;
  const startOffset = -((lines.length - 1) * lineHeight) / 2;
  return (
    <text x={x} y={y} textAnchor="end" dominantBaseline="middle" style={AXIS_STYLE}>
      {lines.map((line, i) => (
        <tspan
          key={i}
          x={x}
          dy={i === 0 ? `${startOffset}em` : `${lineHeight}em`}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: readonly any[]; label?: string | number }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={TOOLTIP_STYLE}>
      {label && <p style={{ ...TOOLTIP_LABEL_STYLE, margin: 0, paddingBottom: 4 }}>{label}</p>}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {payload.map((item, i) => (
          <li key={i} style={{ ...TOOLTIP_ITEM_STYLE, display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: item.color ?? "var(--foreground)",
                flexShrink: 0,
              }}
            />
            <span>
              {item.name ?? item.dataKey} : {item.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatCost(usd: number): string {
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(6)}`;
}

function formatDateLabel(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatTimestamp(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const ms = typeof value === "number" ? value * 1000 : new Date(value).getTime();
  if (Number.isNaN(ms)) return String(value);
  return new Date(ms).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: getTimezone(),
    hour12: getTimeFormat() === "12h",
  });
}

const FEATURE_LABELS: Record<string, string> = {
  brief_narrative: "Brief Narrative",
  market_sentiment: "Market Sentiment",
  agent_service: "Agent Service",
  agentic_supervisor: "Agentic Supervisor",
  langgraph_supervisor: "LangGraph Supervisor",
  insight_ticker_extract: "Ticker Insight Extract",
  insight_ticker_extract_async: "Ticker Insight Extract (Async)",
  insight_ticker_extract_helper: "Ticker Insight Extract (Helper)",
  ticker_insights_v1: "Ticker Insights v1",
  ticker_insights_v2: "Ticker Insights v2",
  watch_tier3_narrative: "Watch Tier 3 Narrative",
  watch_oracle: "Watch Oracle",
};

function featureLabel(feature: string): string {
  if (FEATURE_LABELS[feature]) return FEATURE_LABELS[feature];
  if (feature.startsWith("agentic_")) return `Agentic: ${feature.slice(8)}`;
  if (feature.startsWith("langgraph_")) return `LangGraph: ${feature.slice(10)}`;
  return feature.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function truncateFeature(name: string, max = 24): string {
  return name.length > max ? `${name.slice(0, max - 1)}…` : name;
}

function SectionCard({
  title,
  subtitle,
  icon: Icon,
  info,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  info?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
              <CardTitle className="text-sm font-semibold text-foreground">
                {title}
              </CardTitle>
            </div>
            {subtitle && (
              <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>
          {info && (
            <TooltipProvider delayDuration={0}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="-mr-2 -mt-1 size-7">
                    <Info className="size-4 text-muted-foreground" aria-hidden="true" />
                    <span className="sr-only">{title} info</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" showArrow={false} className="max-w-[280px] bg-background text-foreground border border-border shadow-sm">
                  {info}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[180px] items-center justify-center rounded-[var(--radius-sm)] border border-dashed border-border">
      <p className="max-w-sm text-center text-[12px] text-muted-foreground">
        {message}
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={104} borderRadius={8} data-testid="ai-metrics-skeleton" />
        ))}
      </div>
      <Skeleton height={320} borderRadius={8} data-testid="ai-metrics-skeleton" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton height={280} borderRadius={8} data-testid="ai-metrics-skeleton" />
        <Skeleton height={280} borderRadius={8} data-testid="ai-metrics-skeleton" />
      </div>
    </div>
  );
}

export interface AIMetricsTabProps {
  className?: string;
}

export default function AIMetricsTab({ className }: AIMetricsTabProps) {
  const {
    data,
    loading,
    error,
    range,
    setRange,
    refetch,
  } = useAIMetrics("30d");

  const recentCalls = useAIRecentCalls(range);

  // Reset to page 1 when the date range changes
  useEffect(() => {
    recentCalls.setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const trendData = useMemo(
    () =>
      (data?.points ?? []).map((point) => ({
        ...point,
        label: formatDateLabel(point.date),
      })),
    [data],
  );

  const featureData = useMemo(
    () =>
      (data?.by_feature ?? []).map((f) => ({
        ...f,
        label: featureLabel(f.feature),
      })),
    [data],
  );

  const modelData = useMemo(
    () =>
      (data?.by_model ?? []).map((m) => ({
        ...m,
        label: m.model,
      })),
    [data],
  );

  return (
    <div className={className}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1
            className="m-0 text-[1.5rem] font-bold leading-[1.1] text-foreground md:text-[2rem]"
            style={{ fontFamily: T.serif }}
          >
            AI Metrics
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {rangeLabel(range)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={metricsRangeToDateRange(range)}
            onChange={(dr) => {
              const next = dateRangeToMetricsRange(dr);
              setRange(next ?? "30d");
            }}
            align="end"
            maxDate={new Date()}
          />
          <Button
            variant="outline"
            size="icon-sm"
            onClick={refetch}
            disabled={loading}
            aria-label="Refresh AI metrics"
          >
            <RefreshCw className={loading ? "animate-spin" : undefined} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="mb-5 border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-start gap-2 p-4">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="text-[13px] font-medium text-destructive">
                Failed to load AI metrics
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <LoadingSkeleton />}

      {!loading && !error && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total Tokens"
              value={data ? formatTokens(data.summary.total_tokens) : "—"}
              hint={`${data ? formatTokens(data.summary.total_input_tokens) : "—"} in · ${data ? formatTokens(data.summary.total_output_tokens) : "—"} out`}
              icon={Hash}
            />
            <MetricCard
              label="Total Cost"
              value={data ? formatCost(data.summary.total_cost_usd) : "—"}
              hint="Estimated LLM spend"
              icon={DollarSign}
            />
            <MetricCard
              label="AI Calls"
              value={data?.summary.total_calls ?? 0}
              hint="Total LLM invocations"
              icon={Zap}
            />
            <MetricCard
              label="Avg Tokens/Call"
              value={
                data && data.summary.total_calls > 0
                  ? formatTokens(Math.round(data.summary.total_tokens / data.summary.total_calls))
                  : "—"
              }
              hint="Tokens per invocation"
              icon={Brain}
            />
          </div>

          {/* Token Trend Chart */}
          <SectionCard
            title="Token Consumption Trend"
            subtitle="Daily input · output tokens"
            icon={Hash}
          >
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="totalTokenFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={T.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                  <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={ChartTooltip} cursor={{ stroke: "var(--border)" }} />
                  <Area
                    type="monotone"
                    dataKey="total_tokens"
                    name="Total Tokens"
                    stroke={T.primary}
                    strokeWidth={2}
                    fill="url(#totalTokenFill)"
                  />
                  <Area
                    type="monotone"
                    dataKey="input_tokens"
                    name="Input"
                    stroke={T.purple}
                    strokeWidth={1.5}
                    fill="none"
                  />
                  <Area
                    type="monotone"
                    dataKey="output_tokens"
                    name="Output"
                    stroke={T.amber}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    fill="none"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState message="No AI usage recorded in this period. Data appears once LLM calls are logged." />
            )}
          </SectionCard>

          {/* By Feature + By Model */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionCard
              title="Usage by Feature"
              subtitle="Where AI is being used"
              icon={Cpu}
              info={
                <div className="space-y-2 text-[11px] leading-relaxed">
                  <p>Total token consumption grouped by AI feature or pipeline:</p>
                  <ul className="list-disc pl-3 space-y-1">
                    <li><strong>Market Sentiment</strong> — live web search and news/social sentiment analysis for a ticker or market question.</li>
                    <li><strong>Agent Service</strong> — the agentic question-answering pipeline that plans, fetches data, and answers natural-language questions.</li>
                    <li><strong>Brief Narrative</strong> — portfolio-manager style market narrative generated from the current market context.</li>
                    <li><strong>Ticker Insights v1</strong> — technical analysis of a ticker using indicators such as SMA, RSI, MACD, and options flow.</li>
                    <li><strong>Ticker Insight Extract (Helper)</strong> — extracts the ticker and option symbol from a question as a preprocessing step.</li>
                    <li><strong>Ticker Insight Extract (Async)</strong> — same ticker/symbol extraction, run asynchronously for non-blocking requests.</li>
                  </ul>
                </div>
              }
            >
              {featureData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={featureData}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={WrappedYTick}
                      tickLine={false}
                      axisLine={false}
                      width={140}
                    />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "var(--secondary)" }} />
                    <Bar dataKey="total_tokens" name="Tokens" radius={[0, 3, 3, 0]}>
                      {featureData.map((_, index) => (
                        <Cell
                          key={index}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="No feature-level data yet." />
              )}
            </SectionCard>

            <SectionCard
              title="Usage by Model"
              subtitle="Token consumption per model"
              icon={Brain}
            >
              {modelData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={modelData}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={WrappedYTick}
                      tickLine={false}
                      axisLine={false}
                      width={140}
                    />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "var(--secondary)" }} />
                    <Bar dataKey="total_tokens" name="Tokens" radius={[0, 3, 3, 0]}>
                      {modelData.map((_, index) => (
                        <Cell
                          key={index}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState message="No model-level data yet." />
              )}
            </SectionCard>
          </div>

          {/* Recent AI Calls Table — server-side paginated */}
          <SectionCard
            title="Recent AI Calls"
            subtitle={`${recentCalls.total} total · ${recentCalls.pageSize} per page`}
            icon={Zap}
          >
            {recentCalls.loading && !recentCalls.data ? (
              <Skeleton height={200} borderRadius={8} data-testid="ai-recent-calls-skeleton" />
            ) : recentCalls.error ? (
              <div className="flex items-center gap-2 p-4 text-[13px] text-destructive">
                <AlertCircle className="size-4 shrink-0" />
                {recentCalls.error}
              </div>
            ) : recentCalls.data && recentCalls.data.calls.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Feature</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead className="text-right">{`When (${getTimezoneLabel()})`}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentCalls.data.calls.map((call, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-[11px]">
                          {truncateFeature(featureLabel(call.feature), 30)}
                        </TableCell>
                        <TableCell className="font-mono text-[11px] text-muted-foreground">
                          {call.model}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatTokens(call.total_tokens)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {formatCost(call.cost_usd)}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                          {call.duration_ms > 0 ? `${Math.round(call.duration_ms)}ms` : "—"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-[11px] text-muted-foreground">
                          {formatTimestamp(call.timestamp)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <PaginationControls
                  page={recentCalls.page}
                  pageSize={recentCalls.pageSize}
                  total={recentCalls.total}
                  totalPages={recentCalls.totalPages}
                  loading={recentCalls.loading}
                  onPageChange={(p) => recentCalls.setPage(p)}
                  onPageSizeChange={(s) => recentCalls.setPageSize(s)}
                />
              </>
            ) : (
              <EmptyState message="No recent AI calls recorded." />
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
